import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { UnderwritingActivity, RequestChannel, RequestType, RequestStatus } from "@/models/underwriting";
import { ClaimConfirmDialog } from "./ClaimConfirmDialog";
import { STATUS_STYLES } from "./activityHelpers";
import { isClaimStale } from "@/helpers/dateUtils";
import { isRequestActionable } from "@/helpers/underwritingDashboard";

export function TypeBadge({ type }: { type: RequestType }) {
  return (
    <span className="inline-flex min-w-[124px] items-center justify-center rounded-full border border-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-700">
      {type}
    </span>
  );
}

export function ChannelBadge({ channel }: { channel: RequestChannel | "" }) {
  if (!channel) return <span className="text-muted-foreground">—</span>;
  const label = channel === "Consumer Direct" ? "Consumer" : "Dealer";
  return (
    <span className="inline-flex min-w-[92px] items-center justify-center rounded-full border border-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-700">
      {label}
    </span>
  );
}

export function StatusBadge({ status }: { status: RequestStatus }) {
  const style = STATUS_STYLES[status] ?? STATUS_STYLES.Pending;
  return (
    <span className={cn("inline-flex min-w-[104px] items-center justify-center rounded-full border px-2.5 py-0.5 text-xs font-medium", style.className)}>
      {style.label}
    </span>
  );
}

export function ClaimedByCell({
  activity,
  currentUserName,
  onClaim,
}: {
  activity: UnderwritingActivity;
  currentUserName: string | null;
  onClaim?: (activity: UnderwritingActivity) => Promise<void>;
}) {
  const { claimedBy, claimedOn, status } = activity;
  const isActionable = isRequestActionable(status);
  const isStale = claimedOn !== null && claimedOn !== undefined && isClaimStale(claimedOn);
  const isClaimable = isActionable && isStale;
  const isMine = claimedBy !== null && claimedBy === currentUserName;
  const isClaimedByOther = claimedBy !== null && !isMine;

  if (isMine) {
    return <span className="text-[11px] font-medium text-purple-600">You</span>;
  }
  if (isClaimedByOther) {
    return (
      <div className="flex flex-col items-start gap-1">
        <span className="text-[11px] text-muted-foreground">{claimedBy}</span>
        {isClaimable && (
          <ClaimConfirmDialog
            trigger={
              <Button
                size="sm"
                variant="outline"
                className="h-6 w-24 text-[11px] px-2 border-amber-400 text-slate-700 hover:bg-amber-100"
              >
                Claim Over
              </Button>
            }
            title="Override Claim"
            description={`This request has been held by ${claimedBy} for over 15 minutes. Do you want to take over?`}
            confirmLabel="Take Over"
            onConfirm={onClaim ? () => onClaim(activity) : undefined}
          />
        )}
      </div>
    );
  }
  return (
    <ClaimConfirmDialog
      trigger={
        <Button
          size="sm"
          variant="outline"
          className="h-6 w-24 text-[11px] px-2 border-purple-600 text-slate-700 hover:bg-purple-100"
        >
          Claim
        </Button>
      }
      onConfirm={onClaim ? () => onClaim(activity) : undefined}
      title="Claim Application"
      description="Please confirm you want to claim this application. It will be assigned to you and removed from the available queue."
    />
  );
}
